import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import get from "lodash/get";
import { toast } from "sonner";

import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import { addResizedColumns } from "./useTableConfig";
import MxDomainService from "services/MxDomains";
import UserService from "services/User";
import {
  getMxDomainsTableColumns,
  getMxDomainsTableFilters,
} from "routes/mx_domains/components/MxDomainsTableColumnsAndFilters";

export const MX_DOMAINS_TABLE_CONSTANTS = {
  tableName: "mx_domains_table",
  queryKey: "mx-domains-table",
  className: "mx_domains_table",
  exportTableName: "MxDomainsTable",
  header: "MX Domains",
};

export const invalidateMxDomainsTableQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [MX_DOMAINS_TABLE_CONSTANTS.queryKey],
  });
};

// The check runs in the background, so the table is not invalidated here: the
// new status appears on the next refresh, once the worker has finished.
export const useRevalidateAllMxDomains = () =>
  useMutation({
    mutationFn: () => MxDomainService.revalidateAll(),
    onSuccess: () =>
      toast.success(
        "MX domain check started. Results appear as each domain finishes."
      ),
    onError: err =>
      toast.error(formatError(err, "Failed to start the MX domain check")),
  });

export const useRevalidateMxDomain = () =>
  useMutation({
    mutationFn: id => MxDomainService.revalidate(id),
    onSuccess: () =>
      toast.success("Check started. Refresh the table to see the result."),
    onError: err =>
      toast.error(formatError(err, "Failed to start the domain check")),
  });

export const useMxDomainsTable = () => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const mxDomainsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${MX_DOMAINS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    return addResizedColumns({
      columns: getMxDomainsTableColumns(),
      resizedColumns: mxDomainsTableSettings?.resized_columns || {},
    });
  }, [tableSettings, isTableSettingsFetched]);

  const filters = useMemo(() => getMxDomainsTableFilters(), []);

  return { columns, filters };
};
